import { useCallback, useMemo } from "react";
import {
  WidgetCard,
  diffMoves,
  moveItem,
  type GridLayoutItem,
  type WidgetCardSlotProps,
  type WidgetInstance,
} from "@salestrak/ui";

import { WidgetViewEvent } from "#app/lib/analytics";
import { useUserSetting } from "#app/lib/hooks/use-user-setting";
import {
  hiddenManagedConfigKey,
  parseHiddenManagedConfigs,
  restoreManagedSeed,
  type ManagedWidget,
} from "#app/widgets/dashboards/managed-widgets";
import { GRID_COLUMNS, type WidgetViewStore } from "#app/widgets/dashboards/use-widget-view";

type Store = Pick<
  WidgetViewStore,
  "seedWidgets" | "removeWidget" | "updateWidgetConfig" | "moveWidgets" | "direct"
>;

/**
 * What a managed surface (My Work, Team Overview) hands the board: the
 * operations its maintenance effects write through, the toggle its Manage
 * panel calls, and the card each slot renders. Free dashboards use none of it.
 */
export function useManagedBoardCallbacks({
  view,
  managed,
  personalOnly,
  store,
  widgets,
  layout,
  trackWidget,
}: {
  /** The widget view id; keys the stash of hidden widgets' configs. */
  view: string;
  /** A managed surface; a free dashboard reads no stash. */
  managed: boolean;
  /** The viewer holds a personal copy only: maintenance writes go through
   *  the public store ops, which fork for them; a writer's go direct. */
  personalOnly: boolean;
  store: Store;
  widgets: WidgetInstance[];
  layout: GridLayoutItem[];
  trackWidget: (event: WidgetViewEvent, data?: Record<string, unknown>) => void;
}): {
  operations: WidgetViewStore["direct"];
  onToggleManaged: (widget: ManagedWidget, enabled: boolean) => void;
  renderManagedCard: (props: WidgetCardSlotProps) => React.ReactElement;
} {
  const { seedWidgets, removeWidget, updateWidgetConfig, moveWidgets, direct } = store;
  // Hide removes the instance; its config is kept here so Show brings the
  // card back as it was (a Notepad keeps its text).
  const { value: hiddenConfigs, setValue: setHiddenConfigs } = useUserSetting(
    hiddenManagedConfigKey(view),
    parseHiddenManagedConfigs,
    { skip: !managed },
  );
  const operations = useMemo(
    () =>
      personalOnly
        ? {
            seed: seedWidgets,
            remove: (id: string, opts?: { compact?: boolean }) => {
              removeWidget(id, opts);
            },
            updateConfig: updateWidgetConfig,
            move: moveWidgets,
          }
        : direct,
    [personalOnly, seedWidgets, removeWidget, updateWidgetConfig, moveWidgets, direct],
  );

  // Managed widgets float over the dashboard canvas. The slot props carry
  // everything else, including the width menu when the host wires grid-level
  // `widthOptions`; a module that wants no Maximize says so itself
  // (`chromeActions`), never the host per type.
  const renderManagedCard = useCallback(
    (props: WidgetCardSlotProps) => (
      <WidgetCard {...props} surface="elevated" canvas="background" />
    ),
    [],
  );

  // Toggle a managed widget on or off. The managed board reflows under
  // GRAVITY like any other board: toggling off compacts the survivors up so
  // the hole closes, and toggling on re-enters at the widget's designed slot
  // settled through `moveItem` — identity when the slot is free, honest
  // sibling reflow (then compaction) when something now occupies it. Matched
  // by the stable `widgetKey` baked into each instance's config. Goes through
  // the PUBLIC store ops on purpose: they stage for editors and fork for
  // personalizing viewers. One mental model.
  const onToggleManaged = useCallback(
    (widget: ManagedWidget, enabled: boolean) => {
      if (enabled) {
        trackWidget(WidgetViewEvent.AddWidget, { type: widget.type });
        const seed = restoreManagedSeed(widget, hiddenConfigs);
        const seedId = "__seed__";
        const settled = moveItem(
          [...layout, { id: seedId, x: seed.x, y: seed.y, w: seed.w, h: seed.h }],
          seedId,
          seed.x,
          seed.y,
          GRID_COLUMNS,
          "gravity",
        );
        const cell = settled.find((i) => i.id === seedId) ?? seed;
        // Siblings first, then the seed at its settled cell — both optimistic,
        // so the board opens the hole and the widget lands in it.
        const siblingMoves = diffMoves(
          layout,
          settled.filter((i) => i.id !== seedId),
        );
        if (siblingMoves.length > 0) moveWidgets(siblingMoves);
        seedWidgets([{ ...seed, x: cell.x, y: cell.y }]);
        if (hiddenConfigs[widget.widgetKey]) {
          const { [widget.widgetKey]: _restored, ...rest } = hiddenConfigs;
          setHiddenConfigs(rest);
        }
        return;
      }
      const match = widgets.find((i) => (i.config as { widgetKey?: string } | null)?.widgetKey === widget.widgetKey);
      if (!match) return;
      trackWidget(WidgetViewEvent.DeleteWidget, { type: widget.type });
      if (match.config) {
        setHiddenConfigs({ ...hiddenConfigs, [widget.widgetKey]: match.config as Record<string, unknown> });
      }
      removeWidget(match.id);
    },
    [
      widgets,
      layout,
      seedWidgets,
      moveWidgets,
      removeWidget,
      trackWidget,
      hiddenConfigs,
      setHiddenConfigs,
    ],
  );

  return { operations, onToggleManaged, renderManagedCard };
}
